import { Box, Typography, Paper } from '@mui/material';
import PropTypes from 'prop-types';
import { formatDateTime } from '../../utils/utils';

function EmailSyncHealthBar({
	jobCount,
	userHasOutlookConnection,
	userHasGmailConnection,
	syncSuccessRate,
	lastSuccessfulIngestion,
	failedJobCount,
	totalInteractionCount,
}) {
	// When there are 0 jobs
	// Sync Success Rate: N/A | Last Successful Ingestion: N/A | Failed Jobs: 0 | Total Interaction Records: 0
	const data = {
		connections: [
			{
				name: 'Microsoft Outlook',
				connected: userHasOutlookConnection,
			},
			{
				name: 'Gmail',
				connected: userHasGmailConnection,
			},
		],
		syncSuccessRate: jobCount === 0 ? 'N/A' : syncSuccessRate + '%',
		lastSuccessfulIngestion:
			jobCount === 0 || !lastSuccessfulIngestion
				? 'N/A'
				: formatDateTime(lastSuccessfulIngestion),
		failedJobs: failedJobCount || 0,
		totalInteractionRecords: totalInteractionCount || 0,
	};

	// Reusable typography styles for consistency
	const getHeaderTypographyStyles = (useLargeSize = true) => ({
		fontSize: useLargeSize ? '16px' : '12px',
		fontWeight: 600,
		letterSpacing: '0.5px',
	});

	const HealthBarLabel = ({ label }) => (
		<Typography
			variant='caption'
			sx={{
				color: '#757575',
				fontWeight: 600,
				fontSize: '11px',
				letterSpacing: '0.3px',
				mb: 0.25,
			}}
		>
			{label}
		</Typography>
	);

	const healthItems = [
		{
			label: 'CONNECTIONS',
			testId: 'connections-section',
			content: (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
					{data.connections.map((conn, idx) => (
						<Box
							key={idx}
							sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
						>
							<Box
								data-testid={`connection-dot-${conn.name.toLowerCase().replace(' ', '-')}`}
								sx={{
									width: 6,
									height: 6,
									borderRadius: '50%',
									backgroundColor: conn.connected ? '#4caf50' : '#9e9e9e',
									boxShadow: conn.connected
										? '0 0 0 2px rgba(76, 175, 80, 0.3)'
										: 'none',
								}}
							/>
							<Typography
								variant='body2'
								sx={{
									fontSize: '12px',
									fontWeight: 600,
									color: conn.connected ? 'text.primary' : '#9e9e9e',
									letterSpacing: '0.5px',
								}}
							>
								{conn.name}
							</Typography>
						</Box>
					))}
				</Box>
			),
		},
		{
			label: 'SYNC SUCCESS RATE',
			testId: 'sync-success-rate-section',
			content: (
				<Typography variant='h4' sx={getHeaderTypographyStyles()}>
					{data.syncSuccessRate}
				</Typography>
			),
		},
		{
			label: 'LAST SUCCESSFUL INGESTION',
			testId: 'last-successful-ingestion-section',
			content: (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
					{data.lastSuccessfulIngestion &&
					data.lastSuccessfulIngestion !== 'N/A' ? (
						(() => {
							const parts = data.lastSuccessfulIngestion.split(', ');
							const date = parts.slice(0, -1).join(', '); // "Mar 24, 2026"
							const time = parts[parts.length - 1]; // "3:39 PM"
							return [date, time].map((part, index) => (
								<Typography
									key={index}
									variant='h4'
									sx={{
										...getHeaderTypographyStyles(jobCount === 0),
										lineHeight: 1.3,
									}}
								>
									{part}
								</Typography>
							));
						})()
					) : (
						<Typography
							variant='h4'
							sx={getHeaderTypographyStyles(jobCount === 0)}
						>
							{data.lastSuccessfulIngestion}
						</Typography>
					)}
				</Box>
			),
		},
		{
			label: 'FAILED JOBS',
			testId: 'failed-jobs-section',
			content: (
				<Typography
					variant='h4'
					sx={{
						fontSize: '16px',
						fontWeight: 600,
						color: '#d32f2f',
						letterSpacing: '0.5px',
					}}
				>
					{data.failedJobs.toLocaleString()}
				</Typography>
			),
		},
		{
			label: 'TOTAL INTERACTION RECORDS',
			testId: 'total-interaction-records-section',
			content: (
				<Typography variant='h4' sx={getHeaderTypographyStyles()}>
					{data.totalInteractionRecords.toLocaleString()}
				</Typography>
			),
		},
	];

	return (
		<Paper
			variant='outlined'
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(5, 1fr)',
				gap: 2,
				px: 3,
				mb: 2,
				borderRadius: 1,
			}}
		>
			{healthItems.map((item, index) => (
				<Box
					key={index}
					data-testid={item.testId}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						borderRight:
							index < healthItems.length - 1 ? '1px solid #f1eded' : 'none',
						pr: index < healthItems.length - 1 ? 2 : 0,
						py: 2.5,
					}}
				>
					<HealthBarLabel label={item.label} />
					{item.content}
				</Box>
			))}
		</Paper>
	);
}

EmailSyncHealthBar.propTypes = {
	jobCount: PropTypes.number.isRequired,
	userHasOutlookConnection: PropTypes.bool.isRequired,
	userHasGmailConnection: PropTypes.bool.isRequired,
	syncSuccessRate: PropTypes.number.isRequired,
	lastSuccessfulIngestion: PropTypes.string,
	failedJobCount: PropTypes.number.isRequired,
	totalInteractionCount: PropTypes.number.isRequired,
};

export default EmailSyncHealthBar;
